import React, { type ReactElement, useState } from 'react'
import Avatar from '../../../../components/Avatar'
import DatePicker from '../../../../components/DatePicker'
import DropDown from '../../../../components/DropDown'
import DropDownPicker from '../../../../components/DropDownPicker'
import ChevronDownIcon from '../../../../components/icons/ChevronDownIcon'
import CalendarIcon from '../../../../assets/icon/CalendarIcon'
import CheckboxActiveIcon from '../../../../assets/icon/CheckboxActiveIcon'
import CheckboxIcon from '../../../../assets/icon/CheckboxIcon'
import CreatorIcon from '../../../../assets/icon/CreatorIcon'
import DotsIcon from '../../../../assets/icon/DotsIcon'
import DueDateFilterIcon from '../../../../assets/icon/DueDateFilterIcon'
import CloseIcon from '../../../../assets/icon/CloseIcon'
import PlusIcon from '../../../../assets/icon/PlusIcon'
import ProjectStatusesIcon from '../../../../assets/icon/ProjectStatusesIcon'
import SearchIcon from '../../../../assets/icon/SearchIcon'
import StarIcon from '../../../../assets/icon/StarIcon'
import { TASK_PRIORITY_LIST } from '../../../../utils/taskPriority'
import { getProjectStatusColor, type ProjectBoardStatus } from '../../../../utils/projectStatus'
import type { ProjectMembersPanelSelectionMember } from '../../../../components/ProjectMembersPanel'
import type { TaskFilterField, TaskFilterRule, TaskFilterState } from '../../utils/taskFilters'
import styles from './index.module.scss'

interface TaskFiltersProps {
    state: TaskFilterState
    members: ProjectMembersPanelSelectionMember[]
    statuses: ProjectBoardStatus[]
    onChange: (state: TaskFilterState) => void
    onClose?: () => void
}

interface FilterOption {
    value: string
    label: string
    icon?: ReactElement
    searchText?: string
}

interface FilterPickerProps {
    ariaLabel: string
    placeholder: string
    value: string
    options: FilterOption[]
    onChange: (value: string) => void
}

interface MultiFilterPickerProps {
    ariaLabel: string
    placeholder: string
    values: string[]
    options: FilterOption[]
    variant: 'priority' | 'status' | 'author'
    onChange: (values: string[]) => void
}

const FIELD_OPTIONS: Array<FilterOption & { value: Exclude<TaskFilterField, ''> }> = [
    { value: 'priority', label: 'Приоритет', icon: <StarIcon /> },
    { value: 'status', label: 'Статус', icon: <ProjectStatusesIcon /> },
    { value: 'createdAt', label: 'Дата создания', icon: <CalendarIcon color="#64617E" /> },
    { value: 'dueDate', label: 'Сроки (дедлайн)', icon: <DueDateFilterIcon /> },
    { value: 'author', label: 'Автор задачи', icon: <CreatorIcon /> },
]

const OPERATOR_OPTIONS: FilterOption[] = [
    { value: 'is', label: 'Есть' },
    { value: 'is_not', label: 'Нет' },
]

const EMPTY_RULE: TaskFilterRule = {
    id: 'empty-task-filter',
    field: '',
    operator: 'is',
    value: '',
}

const createRule = (): TaskFilterRule => ({
    id: `${Date.now()}-${Math.random()}`,
    field: '',
    operator: 'is',
    value: '',
})

function FilterPicker({ ariaLabel, placeholder, value, options, onChange }: FilterPickerProps) {
    const selectedOption = options.find((option) => option.value === value)

    return (
        <DropDownPicker
            closeDropDownByPick
            usePortal={false}
            pickerList={options.map((option) => ({
                id: option.value,
                title: option.label,
                icon: option.icon,
            }))}
            getValue={(nextValue) => onChange(String(nextValue))}
            dropdownClassName={styles.taskFilters__pickerDropdown}
            contentClassName={styles.taskFilters__pickerDropdownContent}
            listClassName={styles.taskFilters__pickerList}
            itemClassName={styles.taskFilters__pickerItem}
            btn={(isOpen) => (
                <button
                    type="button"
                    className={`${styles.taskFilters__control} ${isOpen ? styles.taskFilters__controlOpen : ''}`}
                    aria-label={ariaLabel}
                    aria-expanded={isOpen}
                >
                    <span className={styles.taskFilters__controlValue}>
                        <span className={!selectedOption ? styles.taskFilters__placeholder : ''}>
                            {selectedOption?.label || placeholder}
                        </span>
                    </span>
                    <span className={`${styles.taskFilters__chevron} ${isOpen ? styles.taskFilters__chevronOpen : ''}`}>
                        <ChevronDownIcon />
                    </span>
                </button>
            )}
        />
    )
}

function MultiFilterPicker({ ariaLabel, placeholder, values, options, variant, onChange }: MultiFilterPickerProps) {
    const [searchValue, setSearchValue] = useState('')
    const selectedValues = new Set(values)
    const selectedOptions = options.filter((option) => selectedValues.has(option.value))
    const normalizedSearchValue = searchValue.trim().toLowerCase()
    const visibleOptions = variant === 'author' && normalizedSearchValue
        ? options.filter((option) => (option.searchText || option.label).toLowerCase().includes(normalizedSearchValue))
        : options

    const toggleValue = (value: string) => {
        onChange(selectedValues.has(value)
            ? values.filter((currentValue) => currentValue !== value)
            : [...values, value])
    }

    return (
        <DropDown
            usePortal={false}
            dropdownClassName={`${styles.taskFilters__pickerDropdown} ${variant === 'author' ? styles.taskFilters__authorDropdown : ''}`}
            contentClassName={`${styles.taskFilters__pickerDropdownContent} ${variant === 'author' ? styles.taskFilters__authorDropdownContent : ''}`}
            btn={(isOpen) => (
                <button
                    type="button"
                    className={`${styles.taskFilters__control} ${isOpen ? styles.taskFilters__controlOpen : ''}`}
                    aria-label={ariaLabel}
                    aria-expanded={isOpen}
                >
                    <span className={styles.taskFilters__controlValue}>
                        {selectedOptions.length > 0 ? (
                            <span
                                className={`${styles.taskFilters__selectedValues} ${variant === 'author' ? styles.taskFilters__selectedValues_author : ''}`}
                                title={selectedOptions.map((option) => option.label).join(', ')}
                            >
                                {selectedOptions.map((option, index) => (
                                    <span
                                        className={styles.taskFilters__selectedValueIcon}
                                        key={option.value}
                                        aria-hidden="true"
                                        style={{ zIndex: selectedOptions.length - index }}
                                    >
                                        {option.icon}
                                    </span>
                                ))}
                            </span>
                        ) : (
                            <span className={styles.taskFilters__placeholder}>{placeholder}</span>
                        )}
                    </span>
                    <span className={`${styles.taskFilters__chevron} ${isOpen ? styles.taskFilters__chevronOpen : ''}`}>
                        <ChevronDownIcon />
                    </span>
                </button>
            )}
        >
            <div className={variant === 'author' ? styles.taskFilters__multiMenuAuthor : ''}>
                {variant === 'author' && (
                    <label className={styles.taskFilters__authorSearch}>
                        <SearchIcon />
                        <input
                            type="search"
                            className={styles.taskFilters__authorSearchInput}
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            placeholder="Поиск по имени или email"
                        />
                    </label>
                )}
                <div className={`${styles.taskFilters__pickerList} ${styles.taskFilters__multiList} ${variant === 'author' ? styles.taskFilters__authorList : ''}`}>
                    {visibleOptions.map((option) => {
                        const isSelected = selectedValues.has(option.value)

                        return (
                            <button
                                type="button"
                                key={option.value}
                                className={`${styles.taskFilters__pickerItem} ${styles.taskFilters__multiItem} ${variant === 'author' ? styles.taskFilters__multiItemAuthor : ''} ${isSelected ? styles.taskFilters__multiItemSelected : ''}`}
                                aria-pressed={isSelected}
                                onClick={() => toggleValue(option.value)}
                            >
                                {option.icon}
                                <span className={styles.taskFilters__multiItemLabel}>{option.label}</span>
                                <span className={styles.taskFilters__multiItemCheckbox} aria-hidden="true">
                                    {isSelected ? <CheckboxActiveIcon /> : <CheckboxIcon />}
                                </span>
                            </button>
                        )
                    })}
                    {visibleOptions.length === 0 && (
                        <div className={styles.taskFilters__authorEmpty}>Ничего не найдено</div>
                    )}
                </div>
            </div>
        </DropDown>
    )
}

function parseFilterDate(value: string) {
    if (!value) return null
    const date = new Date(`${value}T00:00:00`)
    return Number.isNaN(date.getTime()) ? null : date
}

function formatFilterDateValue(date: Date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function formatFilterDateLabel(value: string) {
    const date = parseFilterDate(value)
    if (!date) return 'дд/мм/гггг'
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${day}/${month}/${date.getFullYear()}`
}

function FilterDatePicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <DropDown
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            usePortal={false}
            dropdownClassName={styles.taskFilters__dateDropdown}
            btn={(open) => (
                <button
                    type="button"
                    className={`${styles.taskFilters__control} ${open ? styles.taskFilters__controlOpen : ''}`}
                    aria-label="Значение фильтра"
                    aria-expanded={open}
                >
                    <span className={styles.taskFilters__controlValue}>
                        <span className={styles.taskFilters__controlIcon}><CalendarIcon color="#64617E" /></span>
                        <span className={!value ? styles.taskFilters__placeholder : ''}>{formatFilterDateLabel(value)}</span>
                    </span>
                </button>
            )}
        >
            <DatePicker
                initialDate={parseFilterDate(value)}
                getDate={(date) => {
                    onChange(formatFilterDateValue(date))
                    setIsOpen(false)
                }}
            />
        </DropDown>
    )
}

export default function TaskFilters({ state, members, statuses, onChange, onClose }: TaskFiltersProps) {
    const displayedRules = state.rules.length ? state.rules : [EMPTY_RULE]

    const updateRule = (id: string, patch: Partial<TaskFilterRule>) => {
        if (state.rules.length === 0) {
            onChange({
                ...state,
                rules: [{ ...createRule(), ...patch }],
            })
            return
        }

        onChange({
            ...state,
            rules: state.rules.map((rule) => rule.id === id ? { ...rule, ...patch } : rule),
        })
    }

    const removeRule = (id: string) => {
        // The placeholder row has nothing to remove — the cross closes the panel.
        if (state.rules.length === 0) {
            onClose?.()
            return
        }

        onChange({
            ...state,
            rules: state.rules.filter((rule) => rule.id !== id),
        })
    }

    const getValueOptions = (rule: TaskFilterRule): FilterOption[] => {
        if (rule.field === 'priority') {
            return TASK_PRIORITY_LIST.map((priority) => ({
                value: priority.id,
                label: priority.title,
                icon: priority.icon,
            }))
        }

        if (rule.field === 'status') {
            return statuses.map((status) => ({
                value: status.key,
                label: status.title || status.key,
                icon: (
                    <span className={styles.taskFilters__statusIcon}>
                        <DotsIcon color={getProjectStatusColor(status.key, status.color)} />
                    </span>
                ),
            }))
        }

        if (rule.field === 'author') {
            return members.map((member) => ({
                value: member.userId,
                label: member.name,
                searchText: `${member.name} ${member.email || ''}`,
                icon: <span className={styles.taskFilters__memberAvatar}><Avatar user={member} /></span>,
            }))
        }

        return []
    }

    return (
        <div className={styles.taskFilters} role="dialog" aria-label="Фильтры задач">
            <div className={styles.taskFilters__rules}>
                {displayedRules.map((rule) => (
                    <div className={`${styles.taskFilters__rule} ${!rule.field ? styles.taskFilters__ruleEmpty : ''}`} key={rule.id}>
                        <FilterPicker
                            ariaLabel="Тип фильтра"
                            placeholder="Выбрать фильтр"
                            value={rule.field}
                            options={FIELD_OPTIONS}
                            onChange={(field) => {
                                const nextField = field as TaskFilterField
                                const supportsMultipleValues = nextField === 'priority' || nextField === 'status' || nextField === 'author'
                                updateRule(rule.id, {
                                    field: nextField,
                                    operator: 'is',
                                    value: supportsMultipleValues ? [] : '',
                                })
                            }}
                        />
                        {rule.field && (
                            <>
                                <FilterPicker
                                    ariaLabel="Условие фильтра"
                                    placeholder="Условие"
                                    value={rule.operator}
                                    options={OPERATOR_OPTIONS}
                                    onChange={(operator) => updateRule(rule.id, { operator: operator as 'is' | 'is_not' })}
                                />
                                {rule.field === 'createdAt' || rule.field === 'dueDate' ? (
                                    <FilterDatePicker
                                        value={typeof rule.value === 'string' ? rule.value : ''}
                                        onChange={(value) => updateRule(rule.id, { value })}
                                    />
                                ) : (
                                    <MultiFilterPicker
                                        ariaLabel="Значение фильтра"
                                        placeholder="Выбрать фильтр"
                                        values={Array.isArray(rule.value) ? rule.value : rule.value ? [rule.value] : []}
                                        options={getValueOptions(rule)}
                                        variant={rule.field}
                                        onChange={(value) => updateRule(rule.id, { value })}
                                    />
                                )}
                            </>
                        )}
                        <button type="button" className={styles.taskFilters__remove} aria-label="Удалить фильтр" onClick={() => removeRule(rule.id)}>
                            <CloseIcon />
                        </button>
                    </div>
                ))}
            </div>
            <button
                type="button"
                className={styles.taskFilters__add}
                onClick={() => onChange({ ...state, rules: [...state.rules, createRule()] })}
            >
                <PlusIcon />
                <span>Добавить еще фильтр</span>
            </button>
        </div>
    )
}
